import { useHeaderNav } from 'contexts/HeaderProvider';
import { FC, useEffect, useState } from 'react';
import { useLocation } from 'react-use';
import styled from 'styled-components';

import Text from 'quarks/Text';

import ComponentButton from 'components/Button/ComponentButton';
import SubMenuGroup from 'components/Header/SubMenuGroup';

type TabbedSubMenuProps = {
  menu?: any[];
  setOpenMenu?: () => void;
};

const TabbedSubMenu: FC<TabbedSubMenuProps> = ({ menu, setOpenMenu }) => {
  const { pathname } = useLocation();
  const { selectedContentType, setSelectedContentType } = useHeaderNav();
  const [activeType, setActiveType] = useState(selectedContentType || 'Operations Suite');

  useEffect(() => {
    const type = pathname?.includes('/pro/marketing')
      ? 'Marketing Pro'
      : pathname?.includes('/pro/fleet')
      ? 'Fleet Pro'
      : pathname?.includes('/operations-suite')
      ? 'Operations Suite'
      : null;

    if (type) {
      setActiveType(type);
      setSelectedContentType(type);
    }
  }, [pathname]);

  const navigationItems = [
    {
      type: 'Operations Suite',
      description: 'An end-to-end solution to help grow your business',
      viewAllHref: '/operations-suite',
    },
    {
      type: 'Marketing Pro',
      description: 'Put your campaigns on easy mode with marketing automation',
      viewAllHref: '/pro/marketing',
    },
    {
      type: 'Fleet Pro',
      description: 'Empower a safer and more productive team',
      viewAllHref: '/pro/fleet',
    },
  ];

  return (
    <StyledContainer>
      <div className="type-col">
        <div className="type-btns">
          <div className={`type-btns-bg ${activeType?.replace(' ', '-').toLowerCase()} clicked`}>
            <div className="type-btns-bg-triangle" />
          </div>
          {navigationItems.map((item, index) => (
            <div
              className={`type-box ${item.type.replace(' ', '-').toLowerCase()}`}
              onClick={() => {
                setSelectedContentType(item.type);
                setActiveType(item.type);
              }}
              key={`type-box-${index}`}
              role="presentation"
            >
              <Text as="h5" textColor="gray-900" fontSize="textXs">
                {item.type}
              </Text>
              <Text as="p" textColor="gray-900" textStyle="textXs">
                {item.description}
              </Text>
            </div>
          ))}
        </div>
      </div>
      <div className="products-items-col">
        {navigationItems.map((item, index) => (
          <div
            className={`menu-items ${item.type.replace(' ', '-').toLowerCase()} ${
              activeType === item.type ? 'active' : 'inactive'
            }`}
            key={`menu-items-${index}`}
          >
            {menu?.[index]?.groups?.map((group: any, subIndex: number) => (
              <SubMenuGroup group={group} key={subIndex} setOpenMenu={setOpenMenu} />
            ))}
            <ComponentButton link={item.viewAllHref} buttonColor="Gray 900 Link" label="View All" />
          </div>
        ))}
      </div>
    </StyledContainer>
  );
};

export default TabbedSubMenu;

const StyledContainer = styled.div`
  position: relative;
  min-height: 335px;

  .MuiButton-linkPrimary {
    border: 1px solid var(--Gray-400, #c1c2c3) !important;
    padding: 8px 14px;
    border-radius: 4px;
    min-width: calc(100% - 48px);
    margin-top: 50px;
    position: absolute;
    bottom: 24px;
    left: 24px;
    font-size: 14px;
    height: 38px;

    &:hover {
      background: rgba(17, 12, 57, 0.02);
      color: black;
    }
  }

  .type-col {
    height: 100%;
    position: absolute;
    width: 315px;
    background-color: rgb(245, 245, 245);
    padding: 15px;

    .MuiButton-linkPrimary {
      min-width: calc(100% - 30px);
    }
  }

  .type-btns {
    height: max-content;
    position: relative;
    display: block;

    .type-btns-bg {
      background: white;
      display: block;
      height: calc(33% - 5px);
      position: absolute;
      width: 100%;
      z-index: 0;
      transition: transform 0.5s ease;
      top: 0;
    }

    .type-btns-bg.fleet-pro {
      transform: translateY(206px);
    }

    .type-btns-bg.operations-suite {
      transform: translateY(3px);
    }

    .type-btns-bg.marketing-pro {
      transform: translateY(105px);
    }

    .type-btns-bg-triangle {
      background-color: white;
      border-radius: 0px 0px 0px 0.25em;
      border: 7px solid rgb(245, 245, 245);
      clip-path: polygon(0% 0%, 100% 100%, 0% 100%);
      display: block;
      height: 35px;
      position: absolute;
      right: -17px;
      top: 30px;
      transform: rotate(-135deg);
      width: 35px;
    }

    .type-box {
      flex-direction: column;
      height: calc(33% - 5px);
      padding: 15px 20px 20px 20px;
      position: relative;
      text-align: left;
      z-index: 999999;

      h5 {
        margin-bottom: 5px;
      }

      &:hover {
        cursor: pointer;
      }
    }
  }

  .products-items-col {
    height: 100%;
    margin-left: 315px;
    position: relative;
    padding: 24px;
    flex-direction: column;

    .active {
      display: flex;
    }

    .inactive {
      display: none;
    }

    .by-feature {
      width: 405px;
    }
  }
`;
